import * as ex from 'excalibur';
import { InteractableDefinition, InteractableType } from '../data/interactables';
import { Logger } from '../core/Logger';
import { EventBus } from '../core/EventBus';
import { GameEventNames, LevelTransitionRequestEvent, LootRequestEvent, InteractableUseEvent, MovementEvent } from '../core/GameEvents';
import { InteractableStatePersistence } from '../core/InteractableStatePersistence';
import { Resources } from '../config/resources';
import { GameActor } from './GameActor';
import { GameEntity } from '../core/GameEntity';
import { InventoryComponent } from './InventoryComponent';
import { GraphicType } from '../constants/GraphicType';
import { GraphicsManager } from '../data/graphics';
import { InteractableState } from '../constants/InteractableState';
import { Inventory } from '../items/Inventory';

// Simple entity interface for interaction
interface InteractingEntity {
    name: string;
    pos?: ex.Vector;
}

export interface InteractionResult {
    success: boolean;
    message?: string;
    newState?: InteractableState;
    consumeAction?: boolean;
}

export class InteractableComponent {
    public readonly type = 'interactable';
    private owner: GameEntity; // The entity that owns this component
    
    private definition: InteractableDefinition;
    private state: InteractableState = InteractableState.Closed;
    private config: any;
    private useCount: number = 0;
    private lastUseTurn: number = -1;
    private currentHealth?: number;
    private levelId: string = 'unknown';
    private inventory?: Inventory;

    constructor(owner: GameEntity, definition: InteractableDefinition, config: any = {}) {
        this.owner = owner;
        this.definition = definition;
        this.config = config;
        this.levelId = config.levelId || 'unknown';
        
        // Initialize state based on type (will be overridden by persistence if available)
        this.initializeState();
        
        // Set health if destructible
        if (definition.destructible && definition.health) {
            this.currentHealth = definition.health;
        }

        // Initialize inventory for containers
        if (definition.type === InteractableType.Container) {
            this.inventory = new Inventory(definition.loot?.length || 10);
            this.generateInitialLoot();
        }
    }

    public onAttach(): void {
        // Now that we have an owner, try to load saved state
        const hasLoadedState = this.loadPersistedState();
        
        if (hasLoadedState) {
            // Update visuals to match loaded state
            this.updateVisuals();
            this.updateCollisionState();
        }
        
        // Listen for movement events to auto-close doors
        if (this.definition.type === InteractableType.Door) {
            EventBus.instance.on(GameEventNames.Movement, (event: any) => {
                this.handlePlayerMovement(event);
            });
        }
    }

    public onDetach(): void {
        // Clean up event listeners
        EventBus.instance.off(GameEventNames.Movement);
    }

    private handlePlayerMovement(event: MovementEvent): void {
        if (!this.owner || this.state !== InteractableState.Open || this.definition.type !== InteractableType.Door) {
            return;
        }

        const doorPos = this.getPosition();
        const playerNewPos = { x: Math.floor(event.to.x), y: Math.floor(event.to.y) };
        const playerOldPos = { x: Math.floor(event.from.x), y: Math.floor(event.from.y) };

        // Only auto-close if player was previously ON the door and is now moving OFF it
        const wasOnDoor = (doorPos.x === playerOldPos.x && doorPos.y === playerOldPos.y);
        const isLeavingDoor = (doorPos.x !== playerNewPos.x || doorPos.y !== playerNewPos.y);

        if (wasOnDoor && isLeavingDoor) {
            this.setState(InteractableState.Closed);
            Logger.debug(`[InteractableComponent] Auto-closed door at ${doorPos.x},${doorPos.y} - player moved from door to ${playerNewPos.x},${playerNewPos.y}`);
        }
    }

    private initializeState(): void {
        switch (this.definition.type) {
            case InteractableType.Door:
                this.state = this.definition.requiresKey ? InteractableState.Locked : InteractableState.Closed;
                break;
            case InteractableType.Container:
                this.state = InteractableState.Closed;
                break;
            default:
                this.state = InteractableState.Closed;
        }
    }

    public canInteract(entity: InteractingEntity): boolean {
        // Check if broken
        if (this.state === InteractableState.Broken) {
            return false;
        }

        // Check use limit
        if (this.definition.useLimit && this.useCount >= this.definition.useLimit) {
            return false;
        }

        // Check cooldown
        if (this.definition.cooldownTurns && this.lastUseTurn >= 0) {
            const currentTurn = this.getCurrentTurn(); // Will need to implement
            if (currentTurn - this.lastUseTurn < this.definition.cooldownTurns) {
                return false;
            }
        }

        // Check key requirement
        if (this.definition.requiresKey && !this.hasRequiredKey(entity)) {
            return false;
        }

        // Check if already consumed (but allow containers with inventories to remain interactable)
        if (this.definition.consumeOnUse && this.state === InteractableState.Used && !this.inventory) {
            return false;
        }

        return true;
    }

    public interact(entity: InteractingEntity): InteractionResult {
        if (!this.canInteract(entity)) {
            return { 
                success: false, 
                message: this.getCannotInteractMessage(),
                consumeAction: false
            };
        }

        Logger.debug(`[InteractableComponent] ${entity.name} interacting with ${this.definition.name}`);

        // Get appropriate handler
        const handler = InteractionHandlers[this.definition.type];
        if (!handler) {
            Logger.warn(`[InteractableComponent] No handler for type: ${this.definition.type}`);
            return { 
                success: false, 
                message: "This interaction is not implemented yet",
                consumeAction: false
            };
        }

        // Execute interaction
        const result = handler.handle(entity, this);
        
        // Update state and tracking
        if (result.success) {
            this.useCount++;
            this.lastUseTurn = this.getCurrentTurn();
            
            if (result.newState) {
                this.setState(result.newState);
            }
            
            // Only mark as used if consumeOnUse is true AND it doesn't have an inventory
            if (this.definition.consumeOnUse && !this.inventory) {
                this.setState(InteractableState.Used);
            }
            
            // Save persistent state after successful interaction
            this.savePersistedState();
        }

        return result;
    }

    public takeDamage(amount: number, entity: InteractingEntity): boolean {
        if (!this.definition.destructible || !this.currentHealth) {
            return false;
        }

        this.currentHealth = Math.max(0, this.currentHealth - amount);
        
        if (this.currentHealth <= 0) {
            this.setState(InteractableState.Broken);
            this.onDestroyed(entity);
            return true;
        }

        return false;
    }

    private onDestroyed(entity: InteractingEntity): void {
        Logger.info(`[InteractableComponent] ${this.definition.name} was destroyed by ${entity.name}`);
        
        // Drop loot if any
        if (this.definition.loot && this.definition.loot.length > 0) {
            this.generateLoot(entity);
        }
        
        // Remove blocking if applicable
        if (this.definition.blocking && this.owner) {
            this.updateCollisionState();
        }
        
        // Fire destruction event
        EventBus.instance.emit(GameEventNames.InteractableDestroyed, {
            interactable: this,
            destroyer: entity
        });
    }

    public forceState(newState: InteractableState): void {
        this.setState(newState);
    }

    private setState(newState: InteractableState): void {
        if (this.state !== newState) {
            const oldState = this.state;
            this.state = newState;
            
            Logger.info(`[InteractableComponent] ${this.definition.name} at position ${this.getPosition().x},${this.getPosition().y} state changed: ${oldState} -> ${newState}, blocking: ${this.shouldBlockMovement()}`);
            
            // Save persistent state
            this.savePersistedState();
            
            // Update visuals and collision
            this.updateVisuals();
            this.updateCollisionState();
            
            // Fire state change event
            EventBus.instance.emit(GameEventNames.InteractableStateChanged, {
                interactable: this,
                oldState,
                newState
            });
        }
    }

    private updateVisuals(): void {
        if (!this.owner) return;
        
        // Get graphics for current state
        const graphic = this.getGraphicsForState();
        this.owner.graphics.use(graphic);
        
        // Force immediate visual update - no setTimeout delay
        if (this.owner && !this.owner.isKilled()) {
            const updatedGraphic = this.getGraphicsForState();
            this.owner.graphics.use(updatedGraphic);
        }
    }

    private updateCollisionState(): void {
        if (!this.owner || !this.definition.blocking) return;
        
        // Update collision based on state
        const shouldBlock = this.shouldBlockMovement();
        // Note: Collision will be handled by CollisionSystem
        this.owner.blocking = shouldBlock;
    }

    public shouldBlockMovement(): boolean {
        if (!this.definition.blocking) return false;
        
        switch (this.state) {
            case InteractableState.Open:
            case InteractableState.Broken:
            case InteractableState.Used:
                return false;
            default:
                return true;
        }
    }

    private getGraphicsForState(): ex.Graphic {
        // 0. Check for GraphicType (Legacy/Global)
        if (this.definition.graphics.graphicType === GraphicType.NineSlice) {
             const width = this.definition.size ? this.definition.size.width * 32 : 32; 
             const height = this.definition.size ? this.definition.size.height * 32 : 32;
             return GraphicsManager.instance.getNineSliceSprite(this.definition.id, width, height);
        }

        // 1. Check for State-specific graphics (New System)
        if (this.definition.graphics.states && this.definition.graphics.states[this.state]) {
            const stateGraphics = this.definition.graphics.states[this.state]!;
            const resource = this.definition.graphics.resource;

            if (resource.isLoaded()) {
                const spriteSheet = ex.SpriteSheet.fromImageSource({
                    image: resource,
                    grid: {
                        rows: 8,
                        columns: 8,
                        spriteWidth: 32,
                        spriteHeight: 32
                    }
                });

                // Handle Animation
                if (stateGraphics.animation) {
                    const frames = stateGraphics.animation.frames.map(f => ({
                        graphic: spriteSheet.getSprite(f.x, f.y) as ex.Graphic,
                        duration: f.duration
                    }));
                    
                    if (frames.length > 0) {
                        return new ex.Animation({
                            frames: frames,
                            strategy: stateGraphics.animation.loop !== false ? ex.AnimationStrategy.Loop : ex.AnimationStrategy.Freeze
                        });
                    }
                }

                // Handle Static Sprite
                if (stateGraphics.spriteCoords) {
                    return spriteSheet.getSprite(stateGraphics.spriteCoords.x, stateGraphics.spriteCoords.y) || new ex.Rectangle({ width: 32, height: 32, color: ex.Color.Magenta });
                }
            }
        }

        // 2. Fallback to Legacy/Default logic
        if (this.definition.graphics.resource && this.definition.graphics.spriteCoords) {
            const { resource, spriteCoords } = this.definition.graphics;
            
            if (resource.isLoaded()) {
                const spriteSheet = ex.SpriteSheet.fromImageSource({
                    image: resource,
                    grid: {
                        rows: 8,
                        columns: 8,
                        spriteWidth: 32,
                        spriteHeight: 32
                    }
                });
                
                // Special handling for common tiles which might have different grid
                if (resource === Resources.CommonTilesPng) {
                     const commonSheet = ex.SpriteSheet.fromImageSource({
                        image: resource,
                        grid: {
                            rows: 3,
                            columns: 8,
                            spriteWidth: 32,
                            spriteHeight: 32
                        }
                    });
                    
                    // Handle state-based sprite changes for doors in CommonTiles (Legacy)
                    let coords = { ...spriteCoords };
                    if (this.definition.type === InteractableType.Door && this.state === InteractableState.Open) {
                        coords.x += 1;
                    }
                    
                    return commonSheet.getSprite(coords.x, coords.y) || new ex.Rectangle({ width: 32, height: 32, color: ex.Color.Magenta });
                }

                // Handle InteractablesPng
                if (resource === Resources.InteractablesPng) {
                    // Handle state-based sprite changes for doors (Legacy)
                    let coords = { ...spriteCoords };
                    if (this.definition.type === InteractableType.Door && this.state === InteractableState.Open) {
                        coords.x += 1;
                    }

                    // Handle Legacy Animations (Fireplace)
                    // This should eventually be moved to the 'states' definition
                    if (this.definition.graphics.animation && this.state !== InteractableState.Closed && this.state !== InteractableState.Locked) {
                         // Fallback for legacy animation string if needed, but we prefer explicit state definitions now.
                         // Keeping this for backward compatibility until all data is migrated.
                         if (this.definition.graphics.animation === 'fireplace-lit') {
                             const frames = [
                                 { x: coords.x, y: coords.y, duration: 200 },
                                 { x: coords.x + 1, y: coords.y, duration: 200 },
                                 { x: coords.x + 2, y: coords.y, duration: 200 },
                                 { x: coords.x + 3, y: coords.y, duration: 200 }
                             ];
                             const validFrames = frames.filter(f => f.x < 8).map(f => ({
                                 graphic: spriteSheet.getSprite(f.x, f.y) as ex.Graphic,
                                 duration: f.duration
                             }));
                             if (validFrames.length > 0) {
                                 return new ex.Animation({ frames: validFrames });
                             }
                         }
                    }

                    return spriteSheet.getSprite(coords.x, coords.y) || new ex.Rectangle({ width: 32, height: 32, color: ex.Color.Magenta });
                }

                return spriteSheet.getSprite(spriteCoords.x, spriteCoords.y) || new ex.Rectangle({ width: 32, height: 32, color: ex.Color.Magenta });
            }
        }

        // 2. Legacy fallback removed
        // If we reach here without a resource, we fall through to the colored rectangle

        
        // 3. Fallback: colored rectangle based on state
        const colors = this.getStateColors();
        const color = colors[this.state] || this.definition.graphics.fallbackColor || ex.Color.Purple;
        
        return new ex.Rectangle({
            width: 32,
            height: 32,
            color: color
        });
    }

    private getStateColors(): Record<InteractableState, ex.Color> {
        const baseColor = this.definition.graphics.fallbackColor || ex.Color.Purple;
        
        return {
            [InteractableState.Closed]: baseColor,
            [InteractableState.Open]: baseColor.lighten(0.3),
            [InteractableState.Locked]: ex.Color.fromHex('#FFD700'), // Gold color
            [InteractableState.Broken]: ex.Color.Gray,
            [InteractableState.Used]: baseColor.darken(0.3),
            [InteractableState.Hidden]: ex.Color.Transparent,
            [InteractableState.Active]: baseColor.lighten(0.5)
        };
    }

    private hasRequiredKey(entity: InteractingEntity): boolean {
        if (!this.definition.requiresKey) return true;
        
        // Check if the entity has the game component system (i.e., is a GameActor)
        if (entity instanceof GameActor) {
            const gameEntity = entity as GameActor;
            const inventoryComponent = gameEntity.gameComponents.get('inventory') as InventoryComponent;
            if (inventoryComponent && inventoryComponent.hasItem) {
                return inventoryComponent.hasItem(this.definition.requiresKey);
            }
        }
        
        return false;
    }

    private getCannotInteractMessage(): string {
        if (this.state === InteractableState.Broken) return "This is broken and cannot be used.";
        if (this.state === InteractableState.Used && this.definition.consumeOnUse) return "This has already been used.";
        if (this.definition.requiresKey && this.state === InteractableState.Locked) return "This is locked. You need a key.";
        if (this.definition.useLimit && this.useCount >= this.definition.useLimit) return "This can no longer be used.";
        
        return "You cannot interact with this right now.";
    }

    private generateLoot(entity: InteractingEntity): void {
        if (!this.definition.loot) return;
        
        // This will be handled by LootSystem
        EventBus.instance.emit(GameEventNames.LootGenerated, {
            items: [], // LootSystem will generate based on definition
            position: this.owner?.pos || ex.vec(0, 0),
            sourceId: this.definition.id,
            actor: entity
        });
    }

    private getCurrentTurn(): number {
        // This should integrate with TurnManager
        // For now, return a placeholder
        return 0;
    }

    // Public getters
    public get interactableDefinition(): InteractableDefinition { return this.definition; }
    public get currentState(): InteractableState { return this.state; }
    public get usageCount(): number { return this.useCount; }
    public get health(): number | undefined { return this.currentHealth; }
    public get ownerEntity(): any { return this.owner; }

    // State persistence methods
    public getPosition(): { x: number; y: number } {
        if (this.owner && this.owner.pos) {
            return { 
                x: Math.floor(this.owner.pos.x / 32), 
                y: Math.floor(this.owner.pos.y / 32) 
            };
        }
        return { x: 0, y: 0 };
    }

    private loadPersistedState(): boolean {
        if (!this.owner) return false;

        const position = this.getPosition();
        const savedState = InteractableStatePersistence.instance.loadState(
            this.levelId,
            position,
            this.definition.id
        );

        if (savedState) {
            this.state = savedState.state;
            this.useCount = savedState.useCount;
            this.lastUseTurn = savedState.lastUseTurn;
            if (savedState.health !== undefined) {
                this.currentHealth = savedState.health;
            }
            
            Logger.debug(`[InteractableComponent] Loaded persistent state for ${this.definition.name}: ${this.state}`);
            return true;
        }

        return false;
    }

    private savePersistedState(): void {
        if (!this.owner) return;

        const position = this.getPosition();
        InteractableStatePersistence.instance.saveState(
            this.levelId,
            position,
            this.definition.id,
            this.state,
            this.useCount,
            this.lastUseTurn,
            this.currentHealth
        );
    }

    public setLevelId(levelId: string): void {
        this.levelId = levelId;
    }

    public getGraphicsForCurrentState(): ex.Graphic {
        return this.getGraphicsForState();
    }

    public getInventory(): Inventory | undefined {
        return this.inventory;
    }

    private generateInitialLoot(): void {
        if (!this.inventory) return;

        // Use LootSystem if lootTableId or containerLootConfig is available
        if (this.definition.lootTableId || this.definition.containerLootConfig) {
            import('../systems/LootSystem').then(({ LootSystem }) => {
                import('../factories/ItemFactory').then(({ ItemFactory }) => {
                    // Use floor from config (passed by InteractableFactory)
                    const floor = this.config.floor || 1;
                    
                    const generatedLoot = LootSystem.instance.generateContainerLoot(this.definition.id, floor);
                    
                    for (const lootData of generatedLoot) {
                        const item = ItemFactory.instance.create(lootData.itemId, lootData.quantity);
                        if (item) {
                            item.identified = lootData.identified;
                            item.enchantments = lootData.enchantments;
                            item.curses = lootData.curses;
                            item.bonusStats = lootData.bonusStats || {};
                            this.inventory!.addItem(item);
                        }
                    }
                });
            });
        } 
        // Fallback to inline loot definition
        else if (this.definition.loot) {
            import('../factories/ItemFactory').then(({ ItemFactory }) => {
                for (const lootItem of this.definition.loot!) {
                    const chance = Math.random();
                    if (chance <= lootItem.chance) {
                        const quantity = Math.floor(Math.random() * 
                            ((lootItem.maxQuantity || 1) - (lootItem.minQuantity || 1) + 1)) + 
                            (lootItem.minQuantity || 1);
                        
                        const item = ItemFactory.instance.create(lootItem.itemId, quantity);
                        if (item) {
                            this.inventory!.addItem(item);
                        }
                    }
                }
            });
        }
    }
}

// Interaction Handlers
interface InteractionHandler {
    handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult;
}

const InteractionHandlers: Record<InteractableType, InteractionHandler> = {
    [InteractableType.Container]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            const def = component.interactableDefinition;
            const inventory = component.getInventory();
            
            // Open inventory UI for containers
            EventBus.instance.emit('interactable:open_inventory', {
                interactable: component,
                inventory: inventory,
                entity: entity
            });
            
            // For containers with inventories, don't change to "Used" state to keep them reopenable
            const newState = inventory ? InteractableState.Open : undefined;
            
            return {
                success: true,
                message: `You opened the ${def.name}.`,
                newState: newState,
                consumeAction: true
            };
        }
    },
    
    [InteractableType.Door]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            const def = component.interactableDefinition;
            const currentState = component.currentState;
            
            if (currentState === InteractableState.Locked) {
                // Unlock, open, and move player through
                const doorPos = component.getPosition();
                const actor = entity as GameActor;
                if (actor.gridPos && actor.animateMovement) {
                    const oldPos = actor.gridPos.clone();
                    actor.gridPos = ex.vec(doorPos.x, doorPos.y);
                    actor.animateMovement(ex.vec(doorPos.x, doorPos.y));
                    
                    // Emit movement event for systems
                    EventBus.instance.emit(GameEventNames.Movement, {
                        actorId: actor.entityId,
                        actor: actor,
                        from: oldPos,
                        to: ex.vec(doorPos.x, doorPos.y)
                    });
                }
                
                return {
                    success: true,
                    message: `You unlocked and opened the ${def.name}.`,
                    newState: InteractableState.Open,
                    consumeAction: true
                };
            } else if (currentState === InteractableState.Closed) {
                // Open and move player through
                const doorPos = component.getPosition();
                const actor = entity as GameActor;
                if (actor.gridPos && actor.animateMovement) {
                    const oldPos = actor.gridPos.clone();
                    actor.gridPos = ex.vec(doorPos.x, doorPos.y);
                    actor.animateMovement(ex.vec(doorPos.x, doorPos.y));
                    
                    // Emit movement event for systems
                    EventBus.instance.emit(GameEventNames.Movement, {
                        actorId: actor.entityId,
                        actor: actor,
                        from: oldPos,
                        to: ex.vec(doorPos.x, doorPos.y)
                    });
                }
                
                return {
                    success: true,
                    message: `You opened the ${def.name}.`,
                    newState: InteractableState.Open,
                    consumeAction: true
                };
            } else {
                // Close
                return {
                    success: true,
                    message: `You closed the ${def.name}.`,
                    newState: InteractableState.Closed,
                    consumeAction: true
                };
            }
        }
    },
    
    [InteractableType.Functional]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            const def = component.interactableDefinition;
            
            // Apply effects
            if (def.effects) {
                // Emit use event for EffectExecutor to handle complex effects (like summoning)
                EventBus.instance.emit(GameEventNames.InteractableUse, new InteractableUseEvent(component, entity as GameActor));
                
                def.effects.forEach(effect => {
                    // Apply effect to actor
                    Logger.info(`Applied effect: ${effect.type} to ${entity.name}`);
                });
            }
            
            return {
                success: true,
                message: `You used the ${def.name}.`,
                consumeAction: true
            };
        }
    },
    
    [InteractableType.Crafting]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            const def = component.interactableDefinition;
            
            // Open crafting interface
            Logger.info(`[InteractableComponent] Would open crafting interface for ${def.id}`);
            
            return {
                success: true,
                message: `You begin using the ${def.name}.`,
                consumeAction: false // Don't consume action for opening UI
            };
        }
    },
    
    [InteractableType.Decorative]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            const def = component.interactableDefinition;
            
            // Apply decorative effects
            if (def.effects) {
                def.effects.forEach(effect => {
                    Logger.info(`Applied decorative effect: ${effect.type} to ${entity.name}`);
                });
            }
            
            return {
                success: true,
                message: `You admire the ${def.name}.`,
                consumeAction: true
            };
        }
    },
    
    [InteractableType.Portal]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            const def = component.interactableDefinition;
            const actor = entity as GameActor;
            
            // Trigger level transition immediately when interacted with
            Logger.info(`[InteractableComponent] *** STAIRS ACTIVATED *** Portal ${def.id} by ${entity.name}`);
            const direction = def.id.includes('down') ? 'down' : 'up';
            Logger.info(`[InteractableComponent] Emitting LevelTransitionRequest - Direction: ${direction}`);
            
            EventBus.instance.emit(GameEventNames.LevelTransitionRequest, new LevelTransitionRequestEvent(
                (entity as GameActor).entityId || entity.name,
                direction,
                'stairs',
                { portal: component }
            ));
            
            return {
                success: true,
                message: `You activated the ${def.name}.`,
                consumeAction: true
            };
        }
    },
    
    [InteractableType.Trap]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            const def = component.interactableDefinition;
            
            // This is for destructible walls and similar
            if (def.destructible) {
                const damaged = component.takeDamage(10, entity); // Default attack damage
                
                if (damaged) {
                    return {
                        success: true,
                        message: `You broke through the ${def.name}!`,
                        consumeAction: true
                    };
                } else {
                    return {
                        success: true,
                        message: `You damaged the ${def.name}.`,
                        consumeAction: true
                    };
                }
            }
            
            return {
                success: false,
                message: `You cannot interact with the ${def.name}.`,
                consumeAction: false
            };
        }
    },

    [InteractableType.Chasm]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            // Chasm interaction (falling)
            // This should ideally be triggered by movement, but if interacted with directly:
            return {
                success: true,
                message: "A deep, dark chasm. Watch your step!",
                consumeAction: false
            };
        }
    },

    [InteractableType.Ice]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            // Breakable ice
             const def = component.interactableDefinition;
             if (def.destructible) {
                 const damaged = component.takeDamage(10, entity);
                 if (damaged) {
                     return {
                         success: true,
                         message: "You smashed the ice!",
                         consumeAction: true
                     };
                 }
             }
            return {
                success: true,
                message: "Thin ice. It looks fragile.",
                consumeAction: false
            };
        }
    },

    [InteractableType.SlipperyIce]: {
        handle(entity: InteractingEntity, component: InteractableComponent): InteractionResult {
            return {
                success: true,
                message: "Slippery ice. You might slide if you step on it.",
                consumeAction: false
            };
        }
    }
};